import React, { useState, useCallback, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import '../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { api, getBaseUrl } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { COLOR_SUCCESS, COLOR_INFO, COLOR_SECONDARY, COLOR_DANGER, COLOR_DARK } from '../../shared/colorUtils';
import { useConfirm } from '../../shared/ConfirmContext';
import BulkGenerateCaseUnitsModal from './BulkGenerateCaseUnitsModal';
import CaseDetailsTab from './components/CaseDetailsTab';
import CaseScheduleTab from './components/CaseScheduleTab';
import CaseCalendarTab from './components/CaseCalendarTab';
import { Tabs, Tab } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { usePermissions } from '../../shared/permissions';

const UNIT_STATUS_COLORS = {
  available: COLOR_SUCCESS,
  in_project: COLOR_INFO,
  maintenance: COLOR_SECONDARY,
  lost: COLOR_DANGER,
  retired: COLOR_DARK,
};

const SHELL_DESCRIPTION_KEY = 'warehouses.cases.subtitle';

const CaseDetail = () => {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();

  const queryClient = useQueryClient();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [stockItemOptions, setStockItemOptions] = useState([]);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [showLinkForm, setShowLinkForm] = useState(false);

  const [scheduleFilter, setScheduleFilter] = useState('upcoming');
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const [units, setUnits] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [deletingUnitId, setDeletingUnitId] = useState(null);

  const [editingQty, setEditingQty] = useState(false);
  const [qtyTotal, setQtyTotal] = useState(0);
  const [qtyAvailable, setQtyAvailable] = useState(0);

  const { isPending: loading, data: itemCase } = useQuery({
    queryKey: ['case-detail', caseId],
    queryFn: () => api.get(`warehouses/api/item-cases/${caseId}/`),
  });

  useEffect(() => {
    if (!itemCase) return;
    setQtyTotal(itemCase.quantity_total ?? 0);
    setQtyAvailable(itemCase.quantity_available ?? 0);
  }, [itemCase]);

  const { isPending: scheduleLoading, data: scheduleData } = useQuery({
    queryKey: ['case-schedule', caseId],
    queryFn: () => api.get(`warehouses/api/item-cases/${caseId}/schedule/`),
  });
  const schedule = Array.isArray(scheduleData) ? scheduleData : [];

  const { isPending: unitsLoading, data: unitsData } = useQuery({
    queryKey: ['case-units', caseId],
    queryFn: () => api.get(`warehouses/api/item-cases/${caseId}/units/`),
  });

  useEffect(() => {
    if (!unitsData) return;
    setUnits(Array.isArray(unitsData) ? unitsData : []);
  }, [unitsData]);

  const { data: stocksData } = useQuery({
    queryKey: ['case-stock-options'],
    queryFn: () => api.get('warehouses/api/warehouses/?page_size=500'),
  });
  const stockOptions = (Array.isArray(stocksData) ? stocksData : []).map((stock) => ({ value: stock.id, label: stock.name }));

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.itemcase' && String(e.pk) === String(caseId)) {
      if (!editingQty) queryClient.invalidateQueries({ queryKey: ['case-detail', caseId] });
    }
    if (e.model === 'warehouses.itemcaseentry') {
      queryClient.invalidateQueries({ queryKey: ['case-schedule', caseId] });
    }
    if (e.model === 'warehouses.caseunit') {
      queryClient.invalidateQueries({ queryKey: ['case-units', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case-detail', caseId] });
    }
  });

  const unitStockMutation = useMutation({
    mutationFn: ({ unitId, stockId }) => api.patch(`warehouses/api/case-units/${unitId}/`, { stock: stockId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-units', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case-detail', caseId] });
      queryClient.invalidateQueries({ queryKey: ['item-cases'] });
    },
    onError: () => { setError(t('warehouses.cases.stockSaveError')); setTimeout(() => setError(null), 5000); },
  });

  const deleteUnitMutation = useMutation({
    mutationFn: (unitId) => api.delete(`warehouses/api/case-units/${unitId}/`),
    onMutate: async (unitId) => {
      setDeletingUnitId(unitId);
      await queryClient.cancelQueries({ queryKey: ['case-units', caseId] });
      const previousUnits = queryClient.getQueryData(['case-units', caseId]);
      queryClient.setQueryData(['case-units', caseId], (old) =>
        Array.isArray(old) ? old.filter(u => u.id !== unitId) : old
      );
      return { previousUnits };
    },
    onError: (err, unitId, context) => {
      if (context?.previousUnits !== undefined) {
        queryClient.setQueryData(['case-units', caseId], context.previousUnits);
      }
      setError('Failed to delete unit.');
      setTimeout(() => setError(null), 4000);
    },
    onSettled: () => {
      setDeletingUnitId(null);
      queryClient.invalidateQueries({ queryKey: ['case-units', caseId] });
    },
  });

  const deleteCaseMutation = useMutation({
    mutationFn: () => api.delete(`warehouses/api/item-cases/${caseId}/`),
    onSuccess: () => navigate('/warehouses/cases'),
    onError: () => { setError('Failed to delete case.'); setTimeout(() => setError(null), 4000); },
  });

  const saveStockItemMutation = useMutation({
    mutationFn: (itemId) => api.patch(`warehouses/api/item-cases/${caseId}/`, { stock_item: itemId }),
    onSuccess: () => {
      setShowLinkForm(false);
      setSelectedStockItem(null);
      setStockItemOptions([]);
      queryClient.invalidateQueries({ queryKey: ['case-detail', caseId] });
    },
    onError: () => { setError(t('warehouses.bundles.addError')); setTimeout(() => setError(null), 5000); },
  });

  const saveQtyMutation = useMutation({
    mutationFn: () => api.patch(`warehouses/api/item-cases/${caseId}/`, { quantity_total: qtyTotal, quantity_available: qtyAvailable }),
    onSuccess: () => {
      setEditingQty(false);
      queryClient.invalidateQueries({ queryKey: ['case-detail', caseId] });
    },
    onError: () => { setError(t('warehouses.bundles.addError')); setTimeout(() => setError(null), 5000); },
  });
  const savingStockItem = saveStockItemMutation.isPending;
  const savingQty = saveQtyMutation.isPending;

  const handleDeleteUnit = async (unitId) => {
    if (!await confirm(t('warehouses.units.deleteConfirm'))) return;
    deleteUnitMutation.mutate(unitId);
  };

  const handlePrintLabels = () => {
    window.open(`${getBaseUrl()}/warehouses/api/item-cases/${caseId}/qr_labels/`, '_blank');
  };

  const handleDeleteCase = async () => {
    if (!await confirm(t('warehouses.cases.confirmDelete', { name: itemCase?.name }))) return;
    deleteCaseMutation.mutate();
  };

  const handleStockItemSearch = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) { setStockItemOptions([]); return; }
    try {
      const response = await api.get(
        `warehouses/api/stock-items/?search=${encodeURIComponent(inputValue)}&page_size=50`
      );
      const items = Array.isArray(response) ? response : (response.results || []);
      setStockItemOptions(items.map(item => ({
        value: item.id,
        label: `${item.product_name} (${item.stock_name || item.stock}) — ${t('warehouses.quantityAvailable')}: ${item.quantity_available ?? '?'}`,
      })));
    } catch (err) {
      console.error('Stock item search error:', err);
    }
  }, [t]);

  const handleSaveStockItem = (itemId) => {
    saveStockItemMutation.mutate(itemId);
  };

  const handleSaveQty = () => {
    saveQtyMutation.mutate();
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const filteredSchedule = schedule.filter(entry => {
    if (scheduleFilter === 'all') return true;
    const start = entry.start_date ? new Date(entry.start_date) : null;
    const end = entry.end_date ? new Date(entry.end_date) : null;
    if (scheduleFilter === 'upcoming') return !end || end >= today;
    if (scheduleFilter === 'active') return start && end && start <= today && end >= today;
    return true;
  });

  const shellDescription = t(SHELL_DESCRIPTION_KEY, 'Track case metadata, unit availability and booking pressure from the shared warehouse detail layout.');

  if (loading) {
    return (
      <PageShell
        pretitle={t('warehouses.cases.detail')}
        title={t('warehouses.cases.title', 'Cases')}
        description={shellDescription}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (!itemCase) {
    return (
      <PageShell
        pretitle={t('warehouses.cases.detail')}
        title={t('warehouses.cases.title', 'Cases')}
        description={shellDescription}
        actions={<Link to="/warehouses/cases" className="btn btn-outline-secondary"><i className="ti ti-arrow-left me-2" />{t('common.back')}</Link>}
      >
        <PageSection>
          <EmptyState
            icon={<i className="ti ti-package-off app-empty-state__icon-glyph" aria-hidden="true" />}
            title={t('warehouses.cases.notFound', 'Case not found')}
            message={error || t('warehouses.cases.notFoundMessage', 'The requested case could not be loaded.')}
            action={<Link to="/warehouses/cases" className="btn btn-primary">{t('common.back')}</Link>}
          />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <>
      <PageShell
        pretitle={t('warehouses.cases.detail')}
        title={itemCase.name}
        description={shellDescription}
        actions={
          <>
            <Link to="/warehouses/cases" className="btn btn-outline-secondary">
              <i className="ti ti-arrow-left me-2" />
              {t('common.back')}
            </Link>
            <button className="btn btn-outline-danger" onClick={handleDeleteCase} disabled={deleteCaseMutation.isPending}>
              <i className="ti ti-trash me-2" />
              {t('common.delete')}
            </button>
          </>
        }
      >
        {error && (
          <div className="alert alert-danger app-inline-alert" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success app-inline-alert" role="alert">
            {success}
          </div>
        )}

        <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'details')} className="mb-0">
          <Tab eventKey="details" title={t('warehouses.cases.tabs.details')} className="pt-3">
            <CaseDetailsTab
              itemCase={itemCase}
              editingQty={editingQty}
              qtyTotal={qtyTotal}
              qtyAvailable={qtyAvailable}
              setEditingQty={setEditingQty}
              setQtyTotal={setQtyTotal}
              setQtyAvailable={setQtyAvailable}
              savingQty={savingQty}
              onSaveQty={handleSaveQty}
              showLinkForm={showLinkForm}
              stockItemOptions={stockItemOptions}
              selectedStockItem={selectedStockItem}
              setShowLinkForm={setShowLinkForm}
              setSelectedStockItem={setSelectedStockItem}
              onSearchStockItem={handleStockItemSearch}
              onSaveStockItem={handleSaveStockItem}
              savingStockItem={savingStockItem}
              units={units}
              unitsLoading={unitsLoading}
              deletingUnitId={deletingUnitId}
              onDeleteUnit={handleDeleteUnit}
              onPrintLabels={handlePrintLabels}
              onOpenBulkModal={() => setShowBulkModal(true)}
              unitStatusColors={UNIT_STATUS_COLORS}
              stockOptions={stockOptions}
              canChangeUnitStock={hasPerm('warehouses.change_caseunit')}
              onChangeUnitStock={(unitId, stockId) => unitStockMutation.mutate({ unitId, stockId })}
              savingUnitStockId={unitStockMutation.isPending ? unitStockMutation.variables?.unitId ?? null : null}
            />
          </Tab>
          <Tab eventKey="schedule" title={t('warehouses.cases.tabs.schedule')} className="pt-3">
            <CaseScheduleTab
              scheduleLoading={scheduleLoading}
              filteredSchedule={filteredSchedule}
              scheduleFilter={scheduleFilter}
              setScheduleFilter={setScheduleFilter}
              expandedProjectId={expandedProjectId}
              setExpandedProjectId={setExpandedProjectId}
              today={today}
              unitStatusColors={UNIT_STATUS_COLORS}
            />
          </Tab>
          <Tab eventKey="calendar" title={t('warehouses.cases.tabs.calendar')} className="pt-3">
            <CaseCalendarTab
              schedule={schedule}
              scheduleLoading={scheduleLoading}
              scheduleFilter={scheduleFilter}
              setScheduleFilter={setScheduleFilter}
            />
          </Tab>
        </Tabs>
      </PageShell>

      {showBulkModal && (
        <BulkGenerateCaseUnitsModal
          caseId={caseId}
          caseName={itemCase?.name}
          defaultCount={Math.max(1, (itemCase?.quantity_total || 1) - units.length)}
          stockOptions={stockOptions}
          onClose={() => setShowBulkModal(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['case-units', caseId] });
            queryClient.invalidateQueries({ queryKey: ['case-detail', caseId] });
            setShowBulkModal(false);
          }}
        />
      )}
    </>
  );
};

export default CaseDetail;
